import {
  KpiType,
  StorePerformanceInterval,
  TransactionFilterType,
  VendorType,
  appCustomersEndpoint,
  areaComparisonEndpoint,
  areaOverviewEndpoint,
  areaResultBreakdownEndpoint,
  areaResultEndpoint,
  averageNetSalePerReceiptEndpoint,
  averagePiecesPerReceipt,
  budgetPlanOverviewEndpoint,
  budgetPlannedEndpoint,
  conversionRateWidgetEndpoint,
  countEndpoint,
  currencyConversionEndpoint,
  customerBaseValueViewEndpoint,
  customerEngagementEndpoint,
  customerExperienceViewEndpoint,
  customerFeedbackEndpoint,
  departmentEndpoint,
  effectiveHoursEndpoint,
  efficiencyOverviewEndpoint,
  helpASaleEndpoint,
  salesNetWidgetEndpoint,
  kpiOverviewEndpoint,
  memberOverviewEndpoint,
  newMembersEndpoint,
  orisCustomersEndpoint,
  queueCustomerOverviewEndpoint,
  queueLengthEndpoint,
  receiptEndpoint,
  receiptSalesAnalysisEndpoint,
  salesAnalysisEndpoint,
  salesEfficiencyViewEndpoint,
  salesInsightsEndpoint,
  salesOverviewEndpoint,
  scoEndpoint,
  shareOfReceiptsEndpoint,
  sptEndpoint,
  storeOverviewEndpoint,
  summaryWidgetsEndpoint,
  takt1Endpoint,
  topPlannedEndpoint,
  topSoldEndpoint,
  transactionEndpoint,
  transactionSearchEndpoint,
} from '../constants';
import { PageInfo, SalesReceiptType, Transaction, TransactionFilter } from '../global-state/types';
import { ExchangeRates } from '../stores/useCurrencyStore';

import { useAuthAxiosRequest } from './useAuthAxiosRequest';

import { useUser } from './useController';

export type StorePerformanceDailyRequest = {
  interval: StorePerformanceInterval.DAILY;
  date: string;
};

export type StorePerformanceWeeklyRequest = {
  interval: StorePerformanceInterval.WEEKLY;
  fromDate: string;
  toDate: string;
};

export type StorePerformanceMonthlyRequest = {
  interval: StorePerformanceInterval.MONTHLY;
  fromDate: string;
  toDate: string;
};

export type StorePerformanceRequest =
  | StorePerformanceDailyRequest
  | StorePerformanceWeeklyRequest
  | StorePerformanceMonthlyRequest;

export type StorePerformanceItem = {
  timeUnit: string;
};

export type LastUpdatedTime = {
  lastUpdated: Date;
};

export type HistoricalComparisionSummary = {
  today: number;
  lastWeek: number;
  lastYear: number;
};

export type TransactionResponse = {
  data: Transaction[];
  paging: Omit<PageInfo, 'currentPageNumber' | 'pageSize' | 'rowsPerPageOptions'>;
};

export type TransactionCount = {
  totalCount: string;
  filterData: TransactionFilter;
};

export type TenderItem = {
  type: VendorType | string;
  count: number;
  amount: number;
  currency: string;
};

export type RegisteredReceiptsItem = {
  item: string;
  count: number;
};

export type RegisteredReceiptsResponse = LastUpdatedTime & {
  registeredReceipts: RegisteredReceiptsItem[];
};

export type HourlyTurnoverPerformanceItem = StorePerformanceItem & {
  amount: number;
};

export type SCOHourlyTurnoverPerformanceItem = StorePerformanceItem & {
  netSaleSCO: number;
  netSale: number;
};

export type SalesNetPerformanceItem = StorePerformanceItem & {
  netSales: number;
  netSalesSCO: number;
  netSalesRFIDSCO: number;
  netSalesMobile: number;
  regularNetSales: number;
};

export type SalesNetResponse = LastUpdatedTime & {
  netSales: SalesNetPerformanceItem[];
  today: number;
  todaySCO: number;
  todayRFIDSCO: number;
  todayMobile: number;
  todayRegular: number;
  lastWeek: number;
  lastWeekSCO: number;
  lastWeekRFIDSCO: number;
  lastWeekMobile: number;
  lastWeekRegular: number;
  lastYear: number;
  lastYearSCO: number;
  lastYearRFIDSCO: number;
  lastYearMobile: number;
  lastYearRegular: number;
};

export type ConversionRatePerformanceItem = StorePerformanceItem & {
  payingCustomers: number;
  nonPayingCustomers: number;
  numberOfTills: number;
};

export type ConversionRateResponse = HistoricalComparisionSummary &
  LastUpdatedTime & {
    conversionRate: ConversionRatePerformanceItem[];
  };

export type NewMembersPerformanceItem = StorePerformanceItem & {
  newMembersCount: number;
  totalTransactions: number;
  unrecruited: number;
};

export type NewMembersResponse = LastUpdatedTime & {
  newMembers: NewMembersPerformanceItem[];
};

export type AppCustomersPerformanceItem = StorePerformanceItem & {
  newCustomerCount: number;
};

export type AppCustomersResponse = LastUpdatedTime & {
  newAppCustomers: number;
  activeCustomers: number;
  appCustomers: number;
};

export type ShareOfReceiptsItem = StorePerformanceItem & {
  totalTransactions: number;
  memberTransactions: number;
};

export type ShareOfReceiptResponse = LastUpdatedTime & { shareOfReceipts: ShareOfReceiptsItem[] };

export type SCOShareOfReceiptsItem = StorePerformanceItem & {
  totalTransactions: number;
  totalSCOTransactions: number;
};

export type SCOShareOfReceiptResponse = LastUpdatedTime & { shareOfReceiptsSCO: SCOShareOfReceiptsItem[] };

export type BudgetPlannedData = {
  budget: number;
  planned: number;
};

export type SalesNetPerReceiptPerformanceItem = StorePerformanceItem & {
  amount: number;
};

export type SalesNetPerReceiptResponse = LastUpdatedTime &
  HistoricalComparisionSummary & {
    netSalePerReceipt: SalesNetPerReceiptPerformanceItem[];
  };

export type ReceiptSalesAnalysisItem = HistoricalComparisionSummary;

export type ReceiptSalesAnalysisResponse = {
  [SalesReceiptType.Spt]: ReceiptSalesAnalysisItem;
  [SalesReceiptType.Takt1]: ReceiptSalesAnalysisItem;
  [SalesReceiptType.EffectiveHours]: ReceiptSalesAnalysisItem;
};

export type ReceiptSalesTimeResponse = LastUpdatedTime & ReceiptSalesAnalysisResponse;

export type AveragePiecesPerReceiptPerformanceItem = StorePerformanceItem & {
  amount: number;
};

export type AveragePiecesPerReceiptDataResponse = LastUpdatedTime &
  HistoricalComparisionSummary & {
    piecesPerReceipt: AveragePiecesPerReceiptPerformanceItem[];
  };

export enum SalesEfficiencyMetrics {
  AverageNetSale = 'averageNetSale',
  AverageVisitors = 'averageVisitors',
  AverageConversionRate = 'averageConversionRate',
  AverageTakt1 = 'averageTakt1',
  AveragePiecesPerReceipt = 'averagePiecesPerReceipt',
  AverageSPT = 'averageSPT',
  AveragePurchasePerReceipt = 'averagePurchasePerReceipt',
  AverageEffectiveHours = 'averageEffectiveHours',
}

export type AreaComparisonItem = {
  name: SalesEfficiencyMetrics;
  today: number | null;
  lastWeek: number;
  lastYear: number;
  lastWeekArea: number;
  lastYearArea: number;
};
export type TillType = 'DEFAULT' | 'SELFCHECKOUT' | 'APPCHECKOUT' | 'null' | 'SCO-MP';

export type QueueLengthTillData = {
  queueCount: number;
  tillId: string;
  tillType: TillType;
  transactions: number;
};

export type QueueLengthPerformanceItem = StorePerformanceItem & {
  tillData: QueueLengthTillData[];
};

export type QueueLengthCheckoutExperience = {
  happyCheckoutExperienceCount: number;
  totalCheckoutExperienceCount: number;
};

export type QueueLengthResponse = LastUpdatedTime & {
  queueLength: QueueLengthPerformanceItem[];
  checkoutExperience: QueueLengthCheckoutExperience;
};

export type QueueLengthForecastPerformanceItem = StorePerformanceItem & {
  defaultQueueCount: number;
  scoQueueCount: number;
  defaultTransactionsCount: number;
  scoTransactionsCount: number;
};

export type CustomerFeedback = {
  rating: number;
  text: string;
};

export type CustomerFeedbackResponse = LastUpdatedTime & {
  feedback: CustomerFeedback[];
};

export type ORISResponse = LastUpdatedTime & {
  totalORISCustomer: number;
  convertedORISCustomers: number;
  loyalOrisCustomerCount: number;
  orisTotalSaleTime: number;
};

export type HourlySales = { hour: string; netSale: number };

export type StoreSalesData = {
  storeId: string;
  currency: string;
  hourlySales: HourlySales[];
  lastUpdated: string;
};

export type CustomerEngagementResponse = LastUpdatedTime & {
  storeTraffic: number;
  memberRecruited: {
    recruitment: number;
    inStoreCareApp: number;
  };
  memberIdentification: {
    membersIdentified: number;
    memberPurchases: number;
  };
  articleLookups: {
    lookups: number;
    purchasedArticles: number;
  };
};

export type SalesItem = {
  timeUnit: string;
  sales: number;
  salesByTotal: number;
  customers: number;
  customersByTotal: number;
  salesByCustomer: number;
  itemsSold: number;
  itemsSoldByCustomer: number;
};

export type SalesInsightsResponse = LastUpdatedTime & {
  salesInsight: SalesItem[];
  totalReceiptsWithoutReturns: number;
};

export type CurrencyConversionResponse = LastUpdatedTime & {
  conversionMap: ExchangeRates;
};

export type CustomerEngagmentPerformanceItem = StorePerformanceItem & {
  customerEngaged: number;
  storeTraffic: number;
};

export type SalesRetaggingPerformanceItem = StorePerformanceItem & {
  amount: number;
};

export type HelpASaleItem = StorePerformanceItem & {
  helpASaleSales: number;
  nonHelpASaleSales: number;
};

export type HelpASaleResponse = LastUpdatedTime & {
  sales: HelpASaleItem[];
  numberOfPieces: number;
  customerFacingLookups: number;
  visitors: number;
};

export type SalesOverviewResponse = {
  regularSales: number;
  regularSalesLW: number;
  regularSalesLY: number;
  scoSales: number;
  scoSalesLW: number;
  scoSalesLY: number;
};

export type MemberOverviewResponse = {
  memberTransactions: number;
  totalTransactions: number;
  scoTransactions: number;
};

export type ConversionRateOverviewResponse = {
  totalTills: number;
  totalTransactions: number;
  totalTransactionsLW: number;
  totalTransactionsLY: number;
  totalVisitors: number;
  totalVisitorsLW: number;
  totalVisitorsLY: number;
};

export type EfficiencyOverviewResponse = {
  effectiveHours: number;
  effectiveHoursLW: number;
  effectiveHoursLY: number;
  spt: number;
  sptLW: number;
  sptLY: number;
  takt1: number;
  takt1LW: number;
  takt1LY: number;
};

export type QueueCustomerOverviewResponse = {
  overallQueueLength: number;
  customerSatisfaction: number;
  customerEngagement: number;
  customerEngagementCount: number;
};

export type BudgetPlanOverviewResponse = {
  conversionRateBudget: number;
  conversionRatePlan: number;
  netSalesBudget: number;
  netSalesPlan: number;
  effectiveHoursBudget: number;
  effectiveHoursPlan: number;
  takt1Budget: number;
  takt1Plan: number;
  sptBudget: number;
  sptPlan: number;
};

export type KpiOverviewType = {
  averageTarget: number;
  totalTarget: number;
};

export type KpiOverviewResponse = {
  [key in KpiType]: KpiOverviewType;
};

export type SummaryWidgetsResponse = {
  kpis: number;
  totalTransactions: number;
  totalDiscountedTransactions: number;
  totalNewMembers: number;
  totalSoldPieces: number;
  totalReturnedPieces: number;
  highlightedWidgets: number;
};

export type CustomerBaseValueProperties = {
  activeCustomers: number;
  unrecruitedCustomers: number;
  memberShareOfReceipt: number;
  appCustomers: number;
  recruitedCustomers: number;
  recruitedShare: number;
};

export type CustomerBaseValueViewItem = CustomerBaseValueProperties & {
  storeId: string;
};

export type CustomerBaseValueViewResponse = {
  customerBaseValue: CustomerBaseValueViewItem[];
  summary: CustomerBaseValueProperties;
};

export type SalesEfficiencyProperties = {
  sales: number;
  salesLastWeek: number;
  salesLastYear: number;
  salesBudget: number;
  conversionRate: number;
  conversionRateLastWeek: number;
  conversionRateLastYear: number;
  takt1: number;
  takt1LastWeek: number;
  takt1LastYear: number;
  takt1Budget: number;
  spt: number;
  sptLastWeek: number;
  sptLastYear: number;
  sptBudget: number;
  effectiveHours: number;
  effectiveHoursLastWeek: number;
  effectiveHoursLastYear: number;
  effectiveHoursBudget: number;
};

export type SalesEfficiencyViewItem = SalesEfficiencyProperties & {
  storeId: string;
};

export type SalesEfficiencyViewResponse = {
  salesAndEfficiency: SalesEfficiencyViewItem[];
  summary: SalesEfficiencyProperties;
};

export type CustomerExperienceProperties = {
  customerSatisfaction: number;
  hoursSpent: number;
  noOfScanners: number;
  queueLength: number;
  orisCR: number;
  approvalRate: number;
  addedSales: number;
  retaggingRate: number;
  negDiff: number;
};

export type CustomerExperienceViewItem = CustomerExperienceProperties & {
  storeId: string;
};

export type CustomerExperienceViewResponse = {
  customerExperience: CustomerExperienceViewItem[];
  summary: CustomerExperienceProperties;
};

export type AreaBreakdownKpi = 'netSales' | 'conversionRate' | 'queueLength' | 'shareOfReceipts' | 'unrecruited';

type FetchAreaKpiBreakdownDataRequest = StorePerformanceRequest & {
  kpi: AreaBreakdownKpi;
  targetCurrency: string;
};

export type AreaBreakdownItemValue = {
  timeUnit: string;
  amount: number;
};
export type AreaBreakdownItem = {
  storeId: string;
  values: AreaBreakdownItemValue[];
};

export type AreaBreakdownData = {
  kpi: AreaBreakdownKpi;
  breakdown: AreaBreakdownItem[];
};

export type SalesPerformanceRow = {
  salesNet: number;
  salesNetLastLastWeek: number;
  salesNetLastYear: number;
  salesNetArea: number;
  salesNetAreaLastYear: number;
  salesNetMarket: number;
  salesNetMarketLastYear: number;
  nonFullPriceSales: number;
  nonFullPriceSalesArea: number;
  nonFullPriceSalesLastYear: number;
  nonFullPriceSalesLastYearArea: number;
  soldPieces: number;
  soldPiecesLastYear: number;
  soldPiecesArea: number;
  soldPiecesAreaLastYear: number;
  soldPiecesMarket: number;
  soldPiecesMarketLastYear: number;
};

export type SalesPerformanceAggregation = {
  departmentNumber: string;
  departmentName: string;
  index: string;
  subIndex: string;
  indexDescription: string;
  subIndexDescription: string;
  garmentGroup: string;
  sales: SalesPerformanceRow; // an object of SalesPerformanceRow
};

export type SalesOverview = {
  budget: number;
  planned: number;
  visitors: {
    thisYear: number;
    lastYear: number;
    thisYearArea: number;
    lastYearArea: number;
    thisYearMarket: number;
    lastYearMarket: number;
  };
  receipts: {
    thisYear: number;
    lastYear: number;
    thisYearArea: number;
    lastYearArea: number;
    thisYearMarket: number;
    lastYearMarket: number;
  };
  departmentDetails: SalesPerformanceAggregation[]; // an array of SalesPerformanceAggregation
};

export type StoreSalesPerformanceResponse = {
  currentDay: SalesOverview;
  lastCompleteDay: SalesOverview;
  currentWeek: SalesOverview;
  lastCompleteWeek: SalesOverview;
  lastFourWeeks: SalesOverview;
  accumulatedFinancialYearToDate: SalesOverview;
};

export type SoldProductItemDetails = {
  productId: string;
  articleId: string;
  productName: string;
  departmentNumber: string;
  departmentName: string;
  subIndexId: string;
  subIndexDescription: string;
  indexId: string;
  indexDescription: string;
  price: number;
  netSaleLastWeek: number;
  netSale: number;
  soldPieces: number;
};

export type SoldProductDetailsResponse = {
  store: SoldProductItemDetails[];
  area: SoldProductItemDetails[];
  salesMarket: SoldProductItemDetails[];
};

export type PlanProductDetailsResponse = {
  productId: string;
  articleId: string;
  productName: string;
  departmentNumber: string;
  departmentName: string;
  subIndexId: string;
  subIndexDescription: string;
  indexId: string;
  indexDescription: string;
  price: number;
  planSales: number;
}[];

export const useTransaction = () => {
  const {
    get: { currentStoreId, keyToken },
  } = useUser();

  const { authAxiosRequest } = useAuthAxiosRequest(keyToken);

  const fetchTransactions = async (
    businessDayDateFrom: string,
    businessDayDateTo: string,
    pageNo: number,
    pageSize: number,
    paymentType?: string,
    retailType?: string,
    cashier?: string,
    countFromOldPartition?: number,
    nextBusinessDayDate?: string,
    storeId?: string,
  ) => {
    const transactionResponse = await authAxiosRequest<TransactionResponse>(
      `${transactionSearchEndpoint}/${storeId ? storeId : currentStoreId}${transactionEndpoint}`,
      {
        params: {
          businessDayDateFrom,
          businessDayDateTo,
          paymentType,
          retailType,
          cashier,
          pageNo,
          pageSize,
          countFromOldPartition,
          nextBusinessDayDate,
        },
      },
    );

    return transactionResponse;
  };

  const fetchCount = async (
    businessDayDateFrom: string,
    businessDayDateTo: string,
    filterType?: TransactionFilterType,
    paymentType?: string,
    retailType?: string,
    cashier?: string,
    storeId?: string,
  ) => {
    const countResponse = await authAxiosRequest<TransactionCount>(
      `${transactionSearchEndpoint}/${storeId ? storeId : currentStoreId}${countEndpoint}`,
      {
        params: {
          businessDayDateFrom,
          businessDayDateTo,
          filterType,
          paymentType,
          retailType,
          cashier,
        },
      },
    );

    return countResponse;
  };

  const fetchReceiptData = async (businessDayDate: string, receiptNumber: string) => {
    const response = await authAxiosRequest<string[]>(
      `${transactionSearchEndpoint}/${currentStoreId}${receiptEndpoint}/${receiptNumber}`,
      {
        params: {
          businessDayDate,
        },
      },
    );

    return response;
  };

  const fetchSalesNetWidgetData = async (request: StorePerformanceRequest) => {
    const salesNetResponse = await authAxiosRequest<SalesNetResponse>(
      `${transactionSearchEndpoint}/v2/${currentStoreId}${salesNetWidgetEndpoint}`,
      {
        params: request,
      },
    );

    return salesNetResponse;
  };

  const fetchNetSalesBudget = async (request: StorePerformanceRequest) => {
    const netSaleBudgetResponse = await authAxiosRequest<BudgetPlannedData>(
      `${transactionSearchEndpoint}/${currentStoreId}${salesNetWidgetEndpoint}${budgetPlannedEndpoint}`,
      {
        params: request,
      },
    );

    return netSaleBudgetResponse;
  };

  const fetchVisitorsBudget = async (request: StorePerformanceRequest) => {
    const netSaleBudgetResponse = await authAxiosRequest<BudgetPlannedData>(
      `${transactionSearchEndpoint}/${currentStoreId}${conversionRateWidgetEndpoint}${budgetPlannedEndpoint}`,
      {
        params: request,
      },
    );

    return netSaleBudgetResponse;
  };

  const fetchConversionRateWidgetData = async (request: StorePerformanceRequest) => {
    const conversionRateResponse = await authAxiosRequest<ConversionRateResponse>(
      `${transactionSearchEndpoint}/${currentStoreId}${conversionRateWidgetEndpoint}`,
      {
        params: request,
      },
    );

    return conversionRateResponse;
  };

  const fetchShareOfReceipts = async (request: StorePerformanceRequest) => {
    const shareOfReceiptsResponse = await authAxiosRequest<ShareOfReceiptResponse>(
      `${transactionSearchEndpoint}/${currentStoreId}${shareOfReceiptsEndpoint}`,
      {
        params: request,
      },
    );

    return shareOfReceiptsResponse;
  };

  const fetchSCOShareOfReceiptsData = async (request: StorePerformanceRequest) => {
    const shareOfReceiptsResponse = await authAxiosRequest<SCOShareOfReceiptResponse>(
      `${transactionSearchEndpoint}/${currentStoreId}${shareOfReceiptsEndpoint}${scoEndpoint}`,
      {
        params: request,
      },
    );

    return shareOfReceiptsResponse;
  };

  const fetchNewMembersData = async (request: StorePerformanceRequest) => {
    const newMembersResponse = await authAxiosRequest<NewMembersResponse>(
      `${transactionSearchEndpoint}/${currentStoreId}${newMembersEndpoint}`,
      {
        params: request,
      },
    );
    return newMembersResponse;
  };

  const fetchAppCustomersData = async (request: StorePerformanceRequest) => {
    const response = await authAxiosRequest<AppCustomersResponse>(
      `${transactionSearchEndpoint}/${currentStoreId}${appCustomersEndpoint}`,
      {
        params: request,
      },
    );

    return response;
  };

  const fetchEffectiveHoursBudget = async (request: StorePerformanceRequest) => {
    const response = await authAxiosRequest<BudgetPlannedData>(
      `${transactionSearchEndpoint}/${currentStoreId}${effectiveHoursEndpoint}${budgetPlannedEndpoint}`,
      {
        params: request,
      },
    );
    return response;
  };

  const fetchSalesNetPerReceiptData = async (request: StorePerformanceRequest) => {
    const response = await authAxiosRequest<SalesNetPerReceiptResponse>(
      `${transactionSearchEndpoint}/${currentStoreId}${averageNetSalePerReceiptEndpoint}`,
      {
        params: request,
      },
    );

    return response;
  };

  const fetchReceiptSalesAnalysisData = async (request: StorePerformanceRequest) => {
    const response = await authAxiosRequest<ReceiptSalesTimeResponse>(
      `${transactionSearchEndpoint}/${currentStoreId}${receiptSalesAnalysisEndpoint}`,
      {
        params: request,
      },
    );
    return response;
  };

  const fetchTakt1Budget = async (request: StorePerformanceRequest) => {
    const response = await authAxiosRequest<BudgetPlannedData>(
      `${transactionSearchEndpoint}/${currentStoreId}${takt1Endpoint}${budgetPlannedEndpoint}`,
      {
        params: request,
      },
    );

    return response;
  };

  const fetchSptBudget = async (request: StorePerformanceRequest) => {
    const response = await authAxiosRequest<BudgetPlannedData>(
      `${transactionSearchEndpoint}/${currentStoreId}${sptEndpoint}${budgetPlannedEndpoint}`,
      {
        params: request,
      },
    );

    return response;
  };

  const fetchAveragePiecesPerReceiptData = async (request: StorePerformanceRequest) => {
    const response = await authAxiosRequest<AveragePiecesPerReceiptDataResponse>(
      `${transactionSearchEndpoint}/${currentStoreId}${averagePiecesPerReceipt}`,
      {
        params: request,
      },
    );

    return response;
  };

  const fetchQueueLengthData = async (request: StorePerformanceRequest) => {
    const response = await authAxiosRequest<QueueLengthResponse>(
      `${transactionSearchEndpoint}/v2/${currentStoreId}${queueLengthEndpoint}`,
      {
        params: request,
      },
    );

    return response;
  };

  const fetchCustomerFeedbackData = async (request: StorePerformanceRequest) => {
    const response = await authAxiosRequest<CustomerFeedbackResponse>(
      `${transactionSearchEndpoint}/${currentStoreId}${customerFeedbackEndpoint}`,
      {
        params: request,
      },
    );
    return response;
  };

  const fetchORISData = async (request: StorePerformanceRequest) => {
    const response = await authAxiosRequest<ORISResponse>(
      `${transactionSearchEndpoint}/${currentStoreId}${orisCustomersEndpoint}`,
      {
        params: request,
      },
    );
    return response;
  };

  const fetchSalesAnalysisData = async (request: StorePerformanceRequest) => {
    const response = await authAxiosRequest<StoreSalesData[]>(
      `${transactionSearchEndpoint}/${currentStoreId}${salesAnalysisEndpoint}`,
      {
        params: request,
      },
    );
    return response;
  };

  const fetchCustomerEngagementData = async (request: StorePerformanceRequest) => {
    const response = await authAxiosRequest<CustomerEngagementResponse>(
      `${transactionSearchEndpoint}/${currentStoreId}${customerEngagementEndpoint}`,
      {
        params: request,
      },
    );
    return response;
  };

  const fetchSalesInsightsData = async (request: StorePerformanceRequest) => {
    const response = await authAxiosRequest<SalesInsightsResponse>(
      `${transactionSearchEndpoint}/${currentStoreId}${salesInsightsEndpoint}`,
      {
        params: request,
      },
    );

    return response;
  };

  const fetchAreaComparisonData = async (request: StorePerformanceRequest, targetCurrency: string) => {
    const response = await authAxiosRequest<AreaComparisonItem[]>(
      `${transactionSearchEndpoint}/${currentStoreId}${areaComparisonEndpoint}`,
      {
        params: {
          ...request,
          targetCurrency,
        },
      },
    );

    return response;
  };

  const fetchCurrencyConversionData = async (currency: string, request: StorePerformanceDailyRequest) => {
    const response = await authAxiosRequest<CurrencyConversionResponse>(
      `${transactionSearchEndpoint}/${currentStoreId}${currencyConversionEndpoint}`,
      {
        params: {
          targetCurrency: currency,
          interval: request.interval,
          date: request.date,
        },
      },
    );

    return response;
  };

  const fetchHelpASaleData = async (request: StorePerformanceRequest) => {
    const helpASaleResponse = await authAxiosRequest<HelpASaleResponse>(
      `${transactionSearchEndpoint}/${currentStoreId}${helpASaleEndpoint}`,
      {
        params: request,
      },
    );
    return helpASaleResponse;
  };

  // AREA OVERVIEW WIDGETS CALLS
  const fetchSalesOverviewData = async (request: StorePerformanceRequest, targetCurrency: string) => {
    const salesOverviewResponse = await authAxiosRequest<SalesOverviewResponse>(
      `${transactionSearchEndpoint}/${currentStoreId}${areaOverviewEndpoint}${salesOverviewEndpoint}`,
      {
        method: 'GET',
        params: {
          ...request,
          targetCurrency,
        },
      },
    );

    return salesOverviewResponse;
  };

  const fetchMembersOverviewData = async (request: StorePerformanceRequest) => {
    const membersOverviewResponse = await authAxiosRequest<MemberOverviewResponse>(
      `${transactionSearchEndpoint}/${currentStoreId}${areaOverviewEndpoint}${memberOverviewEndpoint}`,
      {
        params: request,
      },
    );

    return membersOverviewResponse;
  };

  const fetchConversionRateOverviewData = async (request: StorePerformanceRequest) => {
    const conversionRateResponse = await authAxiosRequest<ConversionRateOverviewResponse>(
      `${transactionSearchEndpoint}/${currentStoreId}${areaOverviewEndpoint}${conversionRateWidgetEndpoint}`,
      {
        params: request,
      },
    );

    return conversionRateResponse;
  };

  const fetchEfficiencyOverviewData = async (request: StorePerformanceRequest, targetCurrency: string) => {
    const efficiencyOverviewResponse = await authAxiosRequest<EfficiencyOverviewResponse>(
      `${transactionSearchEndpoint}/${currentStoreId}${areaOverviewEndpoint}${efficiencyOverviewEndpoint}`,
      {
        params: {
          ...request,
          targetCurrency,
        },
      },
    );

    return efficiencyOverviewResponse;
  };

  const fetchQueueCustomerOverviewData = async (request: StorePerformanceRequest) => {
    const queueCustomerOverviewResponse = await authAxiosRequest<QueueCustomerOverviewResponse>(
      `${transactionSearchEndpoint}/${currentStoreId}${areaOverviewEndpoint}${queueCustomerOverviewEndpoint}`,
      {
        params: request,
      },
    );
    return queueCustomerOverviewResponse;
  };

  const fetchBudgetPlanOverviewData = async (request: StorePerformanceRequest) => {
    const budgetPlanOverviewResponse = await authAxiosRequest<BudgetPlanOverviewResponse>(
      `${transactionSearchEndpoint}/${currentStoreId}${areaOverviewEndpoint}${budgetPlanOverviewEndpoint}`,
      {
        params: request,
      },
    );
    return budgetPlanOverviewResponse;
  };

  const fetchKpiOverviewData = async (request: StorePerformanceRequest, targetCurrency: string) => {
    const kpiOverviewResponse = await authAxiosRequest<KpiOverviewResponse>(
      `${transactionSearchEndpoint}/${currentStoreId}${areaOverviewEndpoint}${kpiOverviewEndpoint}`,
      {
        params: {
          ...request,
          targetCurrency,
        },
      },
    );
    return kpiOverviewResponse;
  };

  const fetchSummaryWidgetsData = async (request: StorePerformanceRequest) => {
    const summaryWidgetsResponse = await authAxiosRequest<SummaryWidgetsResponse>(
      `${transactionSearchEndpoint}/${currentStoreId}${areaOverviewEndpoint}${summaryWidgetsEndpoint}`,
      {
        params: request,
      },
    );
    return summaryWidgetsResponse;
  };

  const fetchCustomerBaseValueViewData = async (request: StorePerformanceRequest) => {
    const customerBaseValueResponse = await authAxiosRequest<CustomerBaseValueViewResponse>(
      `${transactionSearchEndpoint}${areaResultEndpoint}/${currentStoreId}${customerBaseValueViewEndpoint}`,
      {
        params: request,
      },
    );
    return customerBaseValueResponse;
  };

  const fetchSalesEfficiencyViewData = async (request: StorePerformanceRequest, targetCurrency: string) => {
    const salesEfficiencyViewResponse = await authAxiosRequest<SalesEfficiencyViewResponse>(
      `${transactionSearchEndpoint}${areaResultEndpoint}/${currentStoreId}${salesEfficiencyViewEndpoint}`,
      {
        params: {
          ...request,
          targetCurrency,
        },
      },
    );
    return salesEfficiencyViewResponse;
  };

  const fetchCustomerExperienceViewData = async (request: StorePerformanceRequest) => {
    const customerExperienceViewResponse = await authAxiosRequest<CustomerExperienceViewResponse>(
      `${transactionSearchEndpoint}${areaResultEndpoint}/${currentStoreId}${customerExperienceViewEndpoint}`,
      {
        params: request,
      },
    );
    return customerExperienceViewResponse;
  };

  const fetchAreaBreakdownData = async ({ kpi, ...rest }: FetchAreaKpiBreakdownDataRequest) => {
    const response = await authAxiosRequest<AreaBreakdownData>(
      `${transactionSearchEndpoint}${areaResultEndpoint}/${currentStoreId}${areaResultBreakdownEndpoint}/${kpi}`,
      {
        params: rest,
      },
    );
    return response;
  };

  // STORE OVERVIEW WIDGETS CALLS
  const fetchSalesPerformanceData = async (date: string, targetCurrency: string) => {
    const salesOverviewResponse = await authAxiosRequest<StoreSalesPerformanceResponse>(
      `${transactionSearchEndpoint}/${currentStoreId}${storeOverviewEndpoint}${departmentEndpoint}`,
      {
        method: 'GET',
        params: {
          date,
          targetCurrency,
        },
      },
    );

    return salesOverviewResponse;
  };

  const fetchWeeklyTopSoldData = async (date: string, targetCurrency: string) => {
    const topSoldProductsResponse = await authAxiosRequest<SoldProductDetailsResponse>(
      `${transactionSearchEndpoint}/${currentStoreId}${storeOverviewEndpoint}${topSoldEndpoint}`,
      {
        method: 'GET',
        params: {
          date,
          targetCurrency,
        },
      },
    );

    return topSoldProductsResponse;
  };

  const fetchWeeklyTopPlannedData = async (date: string, targetCurrency: string) => {
    const topPlannedProductsResponse = await authAxiosRequest<PlanProductDetailsResponse>(
      `${transactionSearchEndpoint}/${currentStoreId}${storeOverviewEndpoint}${topPlannedEndpoint}`,
      {
        method: 'GET',
        params: {
          date,
          targetCurrency,
        },
      },
    );

    return topPlannedProductsResponse;
  };

  return {
    fetchTransactions,
    fetchCount,
    fetchReceiptData,
    fetchSalesNetWidgetData,
    fetchVisitorsBudget,
    fetchNetSalesBudget,
    fetchConversionRateWidgetData,
    fetchShareOfReceipts,
    fetchSCOShareOfReceiptsData,
    fetchNewMembersData,
    fetchAppCustomersData,
    fetchReceiptSalesAnalysisData,
    fetchTakt1Budget,
    fetchSptBudget,
    fetchAveragePiecesPerReceiptData,
    fetchSalesNetPerReceiptData,
    fetchEffectiveHoursBudget,
    fetchQueueLengthData,
    fetchCustomerFeedbackData,
    fetchORISData,
    fetchSalesAnalysisData,
    fetchCustomerEngagementData,
    fetchSalesInsightsData,
    fetchHelpASaleData,
    fetchAreaComparisonData,
    fetchCurrencyConversionData,

    // Area Overview Widgets
    fetchSalesOverviewData,
    fetchMembersOverviewData,
    fetchConversionRateOverviewData,
    fetchEfficiencyOverviewData,
    fetchQueueCustomerOverviewData,
    fetchBudgetPlanOverviewData,
    fetchKpiOverviewData,
    fetchSummaryWidgetsData,
    fetchCustomerBaseValueViewData,
    fetchSalesEfficiencyViewData,
    fetchCustomerExperienceViewData,
    fetchAreaBreakdownData,

    // Store Overview Widgets
    fetchSalesPerformanceData,
    fetchWeeklyTopSoldData,
    fetchWeeklyTopPlannedData,
  };
};
